/**
 * A beach forklift that follows a queue of simple jobs: drive to a point
 * (forwards or in reverse), turn to a heading, raise or lower the forks, or run
 * a callback. Points can be fixed on the beach or ride with the landing craft,
 * so a route up the ramp stays on the ramp while the hull bobs.
 *
 * Height and pitch come from whatever surface lies underneath — sand, ramp or
 * cargo deck — supplied by the mode.
 */

import { Vector3, type Group, type Object3D } from 'three/webgpu'
import type { Vessel } from '../physics/Vessel.ts'

/** Distance from the forklift's origin (between the axles) to a carried pallet's centre. */
export const FORK_REACH = 1.45
/** Fork height while driving with a load. */
export const CARRY_HEIGHT = .3
const SPEED = 4.6
const CAREFUL_SPEED = 2.4
const TURN_RATE = 3.2
const LIFT_RATE = 1
/** Axle spacing used to tilt the truck on ramps. */
const WHEELBASE = 1.3

export type Surface = (x: number, z: number) => { y: number; careful: boolean }

type Job =
  | { kind: 'drive'; point: Vector3; boat: Vessel | null; reverse: boolean }
  | { kind: 'face'; heading: number }
  | { kind: 'lift'; height: number }
  | { kind: 'call'; run: () => void }

const _target = new Vector3()
const _load = new Vector3()

function wrap(angle: number): number {
  return Math.atan2(Math.sin(angle), Math.cos(angle))
}

export class Forklift {
  readonly position = new Vector3()
  heading = 0
  private pitch = 0
  private lift = 0
  private readonly carriage: Object3D
  private readonly carriageBase: number
  private readonly jobs: Job[] = []
  /** The pallet on the forks, positioned in world space each step. */
  load: Object3D | null = null

  constructor(readonly object: Group, private readonly surface: Surface) {
    this.carriage = object.getObjectByName('fork_carriage')!
    this.carriageBase = this.carriage.position.y
    this.object.rotation.order = 'YXZ'
  }

  get busy(): boolean {
    return this.jobs.length > 0
  }

  place(x: number, z: number, heading: number): void {
    this.position.set(x, 0, z)
    this.heading = heading
    this.pose()
  }

  /** Drive to a point: world space, or the landing craft's frame when `boat` is given. */
  drive(point: Vector3, options: { reverse?: boolean; boat?: Vessel } = {}): this {
    this.jobs.push({ kind: 'drive', point: point.clone(), boat: options.boat ?? null, reverse: options.reverse ?? false })
    return this
  }

  face(heading: number): this {
    this.jobs.push({ kind: 'face', heading })
    return this
  }

  forks(height: number): this {
    this.jobs.push({ kind: 'lift', height })
    return this
  }

  then(run: () => void): this {
    this.jobs.push({ kind: 'call', run })
    return this
  }

  step(dt: number): void {
    let budget = dt
    // Instant jobs (callbacks, already-reached points) chain within one step.
    for (let guard = 0; guard < 8 && budget > 0 && this.jobs.length; guard++) {
      const job = this.jobs[0]
      if (job.kind === 'call') {
        this.jobs.shift()
        job.run()
        continue
      }
      if (job.kind === 'lift') {
        const change = LIFT_RATE * budget
        if (Math.abs(job.height - this.lift) <= change) {
          this.lift = job.height
          this.jobs.shift()
        } else this.lift += Math.sign(job.height - this.lift) * change
        budget = 0
        continue
      }
      if (job.kind === 'face') {
        if (this.turnToward(job.heading, budget)) this.jobs.shift()
        budget = 0
        continue
      }
      if (job.boat) job.boat.localToWorld(job.point, _target)
      else _target.copy(job.point)
      const dx = _target.x - this.position.x
      const dz = _target.z - this.position.z
      const distance = Math.hypot(dx, dz)
      if (distance < .02) {
        this.position.x = _target.x
        this.position.z = _target.z
        this.jobs.shift()
        continue
      }
      const travel = Math.atan2(dx, dz)
      const wanted = job.reverse ? wrap(travel + Math.PI) : travel
      // Swing round on the spot for big turns, then steer while rolling.
      if (Math.abs(wrap(wanted - this.heading)) > .2 && distance > .3) {
        this.turnToward(wanted, budget)
        budget = 0
        continue
      }
      this.turnToward(wanted, budget)
      const speed = this.surface(this.position.x, this.position.z).careful ? CAREFUL_SPEED : SPEED
      const move = Math.min(distance, speed * budget)
      this.position.x += (dx / distance) * move
      this.position.z += (dz / distance) * move
      budget = 0
    }
    this.pose()
  }

  private turnToward(heading: number, dt: number): boolean {
    const error = wrap(heading - this.heading)
    const turn = TURN_RATE * dt
    if (Math.abs(error) <= turn) {
      this.heading = wrap(heading)
      return true
    }
    this.heading = wrap(this.heading + Math.sign(error) * turn)
    return false
  }

  /** Sit on the surface, tilted to the slope between the axles, with any load on the forks. */
  private pose(): void {
    const sin = Math.sin(this.heading), cos = Math.cos(this.heading)
    const half = WHEELBASE / 2
    const front = this.surface(this.position.x + sin * half, this.position.z + cos * half).y
    const rear = this.surface(this.position.x - sin * half, this.position.z - cos * half).y
    const targetPitch = Math.atan2(front - rear, WHEELBASE)
    this.pitch += (targetPitch - this.pitch) * .35
    this.position.y = (front + rear) / 2
    this.object.position.copy(this.position)
    this.object.rotation.set(-this.pitch, this.heading, 0)
    this.carriage.position.y = this.carriageBase + this.lift
    if (!this.load) return
    this.object.updateMatrix()
    this.load.position.copy(_load.set(0, this.lift, FORK_REACH).applyMatrix4(this.object.matrix))
    this.load.quaternion.copy(this.object.quaternion)
  }
}
